import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
} from "@/components/ui/sheet";
import { toast, useToast } from "@/components/ui/use-toast";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import GeneralInfoForm from "./general-info-form";
import EligibilityForm from "./eligibility-form";
import AvailabilityForm from "./availability-form";
import { Button } from "@/components/ui/button";
import CompactStepIndicator from "@/components/ui/compact-step-indicator";
import { LoadingButton } from "@/components/ui/common/loading-button";
import { Facility } from "@/types/facility";
import {
  BookingType,
  ComparisonCondition,
  FacilityStatus,
} from "@/types/enums";
import {
  useCreateFacilityMutation,
  useUpdateFacilityMutation,
} from "@/services/facilityApis";
import { FormProps } from "@/types";
import { ErrorType } from "@/types";

const stepContentComponents = [GeneralInfoForm, EligibilityForm, AvailabilityForm];
interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}

const getStepContent = (step: number, formProps: FormStepProps) => {
  const Component = stepContentComponents[step - 1];
  return Component ? <Component {...formProps} /> : null;
};
const FacilityForm = ({
  isOpen,
  setOpen,
  action,
  setAction,
  data,
  setData,
  refetch,
}: FormProps<Facility>) => {
  const { toast } = useToast();
  const [activeStep, setActiveStep] = useState(1);
  // Inside your component
  const [createFacility] = useCreateFacilityMutation();
  const [updateFacility] = useUpdateFacilityMutation();

  const initialValues: Facility = {
    name: "",
    is_trainer_facility: false,
    coach_id: undefined,
    min_limit: undefined,
    max_capacity: undefined,
    duration_per_slot: undefined,
    category_id: undefined,
    subcategory_id: undefined,
    status: FacilityStatus.Active,
    booking_type: BookingType.Public,
    access_control: false,
    price: undefined,
    is_add_on: false,
    bookable: false,
    shareable_slots: false,
    require_approval: false,
    add_ons: [],
    eligibility: {
      gender_restricted: false,
      allowed_genders: [],
      membership_plan_restricted: false,
      allowed_membership_plans: [],
      age_restricted: false,
      age_condition: ComparisonCondition.EqualTo,
      age_value: 0,
      reward_points_restricted: false,
      reward_points_condition: ComparisonCondition.EqualTo,
      reward_points_value: 0,
      customer_group_restricted: false,
      allowed_customer_groups: [],
    },
    availability: {
      monday: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
      tuesday: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
      wednesday: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
      thursday: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
      friday: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
      saturday: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
      sunday: {
        is_available: false,
        time_slots: { start_time: "", end_time: "" },
      },
    },
  };

  const form = useForm<Facility>({
    mode: "all",
    defaultValues: initialValues,
  });

  const {
    watch,
    handleSubmit,
    clearErrors,
    reset,
    setValue,
    trigger,
    formState: { isSubmitting, errors },
  } = form;
  const watcher = watch();
  const isBookable = watch("bookable");

  const steps = useMemo(() => {
    return isBookable
      ? [
        { label: "General Information & Add Ons", key: 1 },
        { label: "Eligibility", key: 2 },
        { label: "Availability", key: 3 },
      ]
      : [{ label: "General Information & Add Ons", key: 1 }];
  }, [isBookable]);

  useEffect(() => {
    if (!isOpen) return;
    if (action === "edit") {
      const payload = data;
      console.log({ payload }, "Edit");
      reset(payload as Facility);
    } else if (action === "add") {
      setActiveStep(1);
      clearErrors();
      reset(initialValues);
    }
  }, [action, isOpen]);

  // When bookable is turned off, collapse to a single step and clear eligibility/availability
  useEffect(() => {
    if (!isBookable) {
      setActiveStep(1);
      setValue("eligibility", {
        gender_restricted: false,
        allowed_genders: [],
        membership_plan_restricted: false,
        allowed_membership_plans: [],
        age_restricted: false,
        age_condition: ComparisonCondition.EqualTo,
        age_value: 0,
        reward_points_restricted: false,
        reward_points_condition: ComparisonCondition.EqualTo,
        reward_points_value: 0,
        customer_group_restricted: false,
        allowed_customer_groups: [],
      });
      setValue("availability", {
        monday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
        tuesday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
        wednesday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
        thursday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
        friday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
        saturday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
        sunday: { is_available: false, time_slots: { start_time: "", end_time: "" } },
      });
    }
  }, [isBookable, setValue]);

  // Ensure step index never exceeds steps length when bookable toggles
  useEffect(() => {
    if (activeStep > steps.length) {
      setActiveStep(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps.length]);

  const onSubmit = async (formData: Facility) => {
    try {
      // Validate that at least one day is selected when bookable is true
      if (formData.bookable) {
        const availability = formData.availability;
        const hasAtLeastOneDay = Object.values(availability).some(
          (day) => day.is_available === true
        );
        
        if (!hasAtLeastOneDay) {
          toast({
            variant: "destructive",
            title: "Validation Error",
            description: "At least one day must be selected for availability when the facility is bookable.",
          });
          return;
        }
      }

      // Validate that no duplicate add-ons exist
      if (formData.add_ons && formData.add_ons.length > 0) {
        const addOnIds = formData.add_ons
          .map(addOn => addOn.add_on_facility_id)
          .filter(Boolean);
        
        const uniqueIds = new Set(addOnIds);
        if (addOnIds.length !== uniqueIds.size) {
          toast({
            variant: "destructive",
            title: "Validation Error",
            description: "Duplicate add-ons are not allowed. Please remove duplicate entries.",
          });
          return;
        }
      }

      const payload = formData.bookable
        ? {
          
          ...formData,
          name:formData.name.trim()
        }
        : ({
          ...formData,
          name:formData.name.trim(),
          eligibility: {},
          availability: {},
        } as unknown as Facility);

      if (action === "add") {
        await createFacility(payload as Facility).unwrap();
        toast({
          description: "Facility created successfully",
          variant: "success",
        });
      } else {
        await updateFacility(payload as Facility).unwrap();
        toast({
          description: "Facility updated successfully",
          variant: "success",
        });
      }
      refetch();
      handleClose();
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `${typedError.data?.detail ||
            (typedError.data as { message?: string }).message
            }`,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };

  const handleClose = () => {
    clearErrors();
    setActiveStep(1);
    setAction("add");
    setData(undefined);
    reset(initialValues);
    setOpen(false);
  };

  const handleNext = async () => {
    const isStepValid = await trigger(undefined, { shouldFocus: true });

    if (isStepValid && activeStep < steps.length)
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handlePrev = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };
  console.log({ watcher, errors });
  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="bg-formbg w-full lg:!max-w-[1000px] p-0 custom-scrollbar h-full "
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            className="pb-4 "
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="px-5 sticky top-0 z-40 py-2 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-2 w-full">
                <div className="flex items-start gap-3">
                  <button type="button" onClick={handleClose}>
                    <i className="fa fa-xmark text-xl"></i>
                  </button>
                  <div>
                    <h1 className="font-medium">
                      {action == "add" ? "Create" : "Edit"} Facility
                    </h1>
                    <CompactStepIndicator
                      activeStep={activeStep}
                      steps={steps}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {activeStep !== 1 && (
                    <Button
                      className="w-[80px]"
                      variant="outline"
                      onClick={handlePrev}
                      disabled={activeStep === 1}
                    >
                      Previous
                    </Button>
                  )}
                  {activeStep !== steps.length && (
                    <Button
                      className="w-[80px]"
                      onClick={handleNext}
                      disabled={activeStep === steps.length}
                    >
                      Next
                    </Button>
                  )}
                  {activeStep === steps.length && (
                    <LoadingButton
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      type="submit"
                      className="w-[80px]"
                    >
                      {!isSubmitting && (
                        <i className="fa fa-floppy-disk pr-2"></i>
                      )}
                      {action=="add"?"Save":"Update"}
                    </LoadingButton>
                  )}
                </div>
              </div>
            </SheetHeader>

            <SheetDescription className="h-full  p-6  ">
              {getStepContent(activeStep , {action, open: isOpen})}
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default FacilityForm;
